import { useEffect, useState } from "react";
import { Plus, Star, Pencil, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FIELDS = [["first_name", "Nome *"], ["last_name", "Cognome *"], ["job_title", "Ruolo"], ["department", "Reparto"], ["email", "Email"], ["phone", "Telefono"], ["mobile", "Cellulare"], ["pec", "PEC"]];

const ContactDialog = ({ open, onOpenChange, companyId, contact, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF(contact ? { ...contact } : { is_primary: false, status: "attivo" }); }, [open, contact]);
  const save = async () => {
    const body = { ...Object.fromEntries(FIELDS.map(([k]) => [k, f[k] || null])), notes: f.notes || null, is_primary: !!f.is_primary, status: f.status || "attivo" };
    try {
      if (contact) await api.patch(`/crm/contacts/${contact.id}`, body); else await api.post(`/crm/companies/${companyId}/contacts`, body);
      toast.success(contact ? "Contatto aggiornato" : "Contatto aggiunto"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="contact-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{contact ? "Modifica contatto" : "Nuovo contatto"}</DialogTitle><DialogDescription>Referente collegato a questa azienda.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          {FIELDS.map(([k, l]) => <div key={k} className="space-y-1.5"><Label className="text-xs">{l}</Label><Input maxLength={k.includes("mail") || k === "pec" ? 254 : 80} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`contact-field-${k}`} /></div>)}
          <label className="col-span-2 flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!f.is_primary} onChange={(e) => setF({ ...f, is_primary: e.target.checked })} data-testid="contact-field-primary" />Referente principale</label>
          <label className="col-span-2 flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.status === "inattivo"} onChange={(e) => setF({ ...f, status: e.target.checked ? "inattivo" : "attivo" })} data-testid="contact-field-inactive" />Non più attivo</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button><Button onClick={save} disabled={!f.first_name?.trim() || !f.last_name?.trim()} data-testid="contact-save">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ContactsPanel = ({ companyId, contacts, onChanged, compact }) => {
  const { can } = useAuth();
  const [editing, setEditing] = useState(undefined);
  const list = compact ? contacts.filter((c) => c.status !== "inattivo").slice(0, 3) : contacts;
  return (
    <div data-testid="contacts-panel">
      {!compact && can("contacts:create") && <Button size="sm" onClick={() => setEditing(null)} className="mb-3 gap-1" data-testid="contact-new-button"><Plus />Nuovo contatto</Button>}
      {list.length === 0 && <p className="text-sm text-slate-500" data-testid="contacts-empty">Nessun referente registrato.</p>}
      <ul className="divide-y divide-line">
        {list.map((c) => (
          <li key={c.id} className="flex items-start justify-between gap-3 py-3" data-testid={`contact-row-${c.id}`}>
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-ink">{c.is_primary && <Star className="h-3.5 w-3.5 fill-brand-500 text-brand-500" aria-label="Referente principale" />}{c.first_name} {c.last_name}
                {c.status === "inattivo" && <StatusBadge tone="neutral" dot={false}>Non attivo</StatusBadge>}</p>
              <p className="text-xs text-slate-500">{[c.job_title, c.department].filter(Boolean).join(" · ") || "—"}</p>
              <p className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-600">
                {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-brand-700"><Mail className="h-3 w-3" />{c.email}</a>}
                {(c.mobile || c.phone) && <a href={`tel:${c.mobile || c.phone}`} className="flex items-center gap-1 hover:text-brand-700"><Phone className="h-3 w-3" />{c.mobile || c.phone}</a>}
              </p>
            </div>
            {!compact && can("contacts:update") && <Button size="icon" variant="ghost" onClick={() => setEditing(c)} aria-label="Modifica contatto" data-testid={`contact-edit-${c.id}`}><Pencil /></Button>}
          </li>
        ))}
      </ul>
      <ContactDialog open={editing !== undefined} onOpenChange={(o) => !o && setEditing(undefined)} companyId={companyId} contact={editing} onSaved={onChanged} />
    </div>
  );
};
